import React, { useRef, useState, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { updatePassword } from '../../redux/actions/auth';
import { showToastNotif } from '../../redux/actions/notification';
import icView from '../../assets/views-icon.svg';
import icViewDisabled from '../../assets/view-disabled-icon.svg';
import styles from './passwordModal.module.css';
import { generateErrorDetail, parseQuery } from '../../utils';
import Button from '../Button/Button';
import GeneralNotifModal from '../GeneralModal/generalNotifModal';
import PasswordConfirm from './PasswordConfirm';

// eslint-disable-next-line prefer-regex-literals
const strongRegex = new RegExp(
  '^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#%&])(?=.{8,})'
);
const minLength = 8;
function PasswordModal({ open, onDismiss }) {
  const [showPass, setShowPass] = useState(false);
  const [showConfirmCancel, setShowConfirmCancel] = useState(false);
  const pass = useRef();
  const [error, setError] = useState(false);
  const confPass = useRef();
  const oldPass = useRef();
  const dispatch = useDispatch();

  const location = useLocation();
  const navigate = useNavigate();

  const { defaultPassword } = parseQuery(location?.search);

  const toggleShowCancel = useCallback(() => {
    setShowConfirmCancel((prev) => !prev);
  }, []);

  const toggleShowPass = useCallback(() => {
    setShowPass((prev) => !prev);
  }, []);

  const Dismiss = useCallback(() => {
    if (defaultPassword) {
      const queryParams = '';
      navigate({
        search: queryParams,
        replace: true
      });
    }
    setError(false);
    onDismiss();
  }, [defaultPassword, onDismiss, navigate]);

  const mapErrors = (err) => {
    switch (err.code) {
      case 'gfx4000003':
        setError('Old Password should NOT be shorter than 8 characters');
        break;
      case 'gfu4030207':
        setError('Your old password is incorrect');
        break;
      default:
        setError('Something went wrong, please try again later');
        break;
    }
  };

  const onSubmit = useCallback(async () => {
    if (pass.current.value !== confPass.current.value) {
      return setError('Your confirmation password is not matching');
    }
    if (!strongRegex.test(pass.current.value)) {
      return setError(
        'Your password must contain at least 8 characters with lowercase, uppercase, number and special character'
      );
    }
    try {
      const res = await dispatch(
        updatePassword(
          oldPass.current.value,
          pass.current.value,
          confPass.current.value
        )
      );
      if (!res.error) {
        dispatch(showToastNotif('success', 'Your password has been updated'));
        oldPass.current.value = '';
        confPass.current.value = '';
        pass.current.value = ''; // clear form values on success
        Dismiss();
      }
    } catch (err) {
      const errorData = generateErrorDetail(err);
      mapErrors(errorData);
    }
  }, [pass, confPass, dispatch, Dismiss]);

  return (
    <>
      <GeneralNotifModal open={showConfirmCancel}>
        <PasswordConfirm
          onClose={() => {
            toggleShowCancel(false);
            Dismiss();
          }}
          onResume={() => {
            toggleShowCancel(false);
          }}
        />
      </GeneralNotifModal>
      <div className={`${styles['passModal']} ${open ? styles['show'] : ''}`}>
        <div className={styles['passWrapper']}>
          <div className={styles['passContent']}>
            <div>
              <h3>Change Password</h3>
              <span className={styles['passHelper']}>
                For security, please enter your old password.
              </span>
            </div>
            <span className={styles['passWarning']}>{error}</span>
            <section>
              <span>Old Password</span>
              <div className={styles['inputGroup']}>
                <input
                  ref={oldPass}
                  type={showPass ? 'text' : 'password'}
                  minLength={minLength}
                  className={styles['pwd']}
                  defaultValue={
                    defaultPassword && decodeURIComponent(defaultPassword)
                  }
                />
                <button
                  type='button'
                  onClick={toggleShowPass}
                  className={styles['showPasswordBtn']}
                >
                  <img src={showPass ? icView : icViewDisabled} alt='view' />
                </button>
              </div>
            </section>
            <section>
              <span>New Password</span>
              <div className={styles['inputGroup']}>
                <input
                  ref={pass}
                  type={showPass ? 'text' : 'password'}
                  minLength={minLength}
                  className={styles['pwd']}
                />
                <button
                  type='button'
                  onClick={toggleShowPass}
                  className={styles['showPasswordBtn']}
                >
                  <img src={showPass ? icView : icViewDisabled} alt='view' />
                </button>
              </div>
            </section>
            <section>
              <span>Reenter New Password</span>
              <div className={styles['inputGroup']}>
                <input
                  ref={confPass}
                  type={showPass ? 'text' : 'password'}
                  minLength={minLength}
                  className={styles['pwd']}
                />
                <button
                  type='button'
                  onClick={toggleShowPass}
                  className={styles['showPasswordBtn']}
                >
                  <img src={showPass ? icView : icViewDisabled} alt='view' />
                </button>
              </div>
            </section>
            <section className={styles['buttonSection']}>
              <Button
                textColor='white'
                bgColor='#2E2E2E'
                shape='rounded'
                onClick={toggleShowCancel}
                width='11em'
              >
                Cancel
              </Button>
              <Button
                bgColor='sushiroll'
                shape='rounded'
                width='11em'
                onClick={onSubmit}
                textStyle={{ fontWeight: '600' }}
              >
                Change Password
              </Button>
            </section>
          </div>
        </div>
      </div>
    </>
  );
}

export default PasswordModal;
